import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useInView } from 'react-intersection-observer'

export default function Viewable({ children, threshold, duration, onViewable, suspend }) {
  const [didRun, setDidRun] = useState(false)
  const [ref, inView] = useInView({ threshold, triggerOnce: didRun })
  const [timeoutID, setTimeoutID] = useState(null)

  const onTimeoutCompleted = useCallback(() => {
    setDidRun(true)
    onViewable()
  }, [setDidRun, onViewable])

  useEffect(() => {
    if (inView && !suspend) {
      setTimeoutID(setTimeout(onTimeoutCompleted, duration))
    } else {
      setTimeoutID(null) // See cleanup
    }
  }, [inView, suspend, setTimeoutID, onTimeoutCompleted, duration])

  // Clean up. Will run each time `timeoutID` changes and on unmount
  useEffect(() => () => typeof timeoutID === 'number' && window.clearTimeout(timeoutID), [
    timeoutID,
  ])

  return (
    <div ref={ref} data-ref="viewable">
      {children}
    </div>
  )
}

Viewable.defaultProps = {
  threshold: 0.5,
  duration: 1000,
  suspend: false,
}

Viewable.propTypes = {
  children: PropTypes.node,
  /**
   * Number between 0 and 1 indicating the percentage that should be visible before triggering
   */
  threshold: PropTypes.number,
  /**
   * For how long does the element need to be in view before triggering `onViewable`
   */
  duration: PropTypes.number,
  /**
   * If enabled, will prevent the timeout from starting. Useful when you need to wait for a banner to load
   */
  suspend: PropTypes.bool,
  /**
   * Callback what will run once the them element has been in view for the entierty of the supplied duration
   */
  onViewable: PropTypes.func,
}
